import type { JSONContent } from '@tiptap/core';

export const CREW_BRIEFING_RICH_TEXT_FORMAT = 'crew-briefing-rich-text';
export const CREW_BRIEFING_RICH_TEXT_VERSION = 1;

type RichTextEnvelope = {
  format: typeof CREW_BRIEFING_RICH_TEXT_FORMAT;
  version: typeof CREW_BRIEFING_RICH_TEXT_VERSION;
  document: JSONContent;
};

const allowedMarks = new Set(['bold', 'italic', 'underline']);

export function decodeCrewBriefingRichTextValue(value: string): JSONContent {
  const document = tryDecodeEnvelope(value);
  return document ?? createPlainTextDocument(value);
}

export function encodeCrewBriefingRichTextValue(document: JSONContent): string {
  if (isDocumentEmpty(document)) {
    return '';
  }

  const envelope: RichTextEnvelope = {
    format: CREW_BRIEFING_RICH_TEXT_FORMAT,
    version: CREW_BRIEFING_RICH_TEXT_VERSION,
    document,
  };

  return JSON.stringify(envelope);
}

export function isCrewBriefingRichTextValue(value: string): boolean {
  return tryDecodeEnvelope(value) !== null;
}

function tryDecodeEnvelope(value: string): JSONContent | null {
  if (value.trim() === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !isRecord(parsed) ||
      parsed.format !== CREW_BRIEFING_RICH_TEXT_FORMAT ||
      parsed.version !== CREW_BRIEFING_RICH_TEXT_VERSION ||
      !isValidDocument(parsed.document)
    ) {
      return null;
    }

    return parsed.document;
  } catch {
    return null;
  }
}

function createPlainTextDocument(value: string): JSONContent {
  const normalizedValue = value.replace(/\r\n?/g, '\n');
  if (normalizedValue === '') {
    return createEmptyDocument();
  }

  const content: JSONContent[] = [];
  normalizedValue.split('\n').forEach((line, index) => {
    if (index > 0) {
      content.push({ type: 'hardBreak' });
    }

    if (line !== '') {
      content.push({ type: 'text', text: line });
    }
  });

  return {
    type: 'doc',
    content: [{ type: 'paragraph', content }],
  };
}

function createEmptyDocument(): JSONContent {
  return {
    type: 'doc',
    content: [{ type: 'paragraph' }],
  };
}

function isDocumentEmpty(document: JSONContent): boolean {
  return !containsText(document);
}

function containsText(node: JSONContent): boolean {
  if (typeof node.text === 'string' && node.text.length > 0) {
    return true;
  }

  return node.content?.some(containsText) ?? false;
}

function isValidDocument(value: unknown): value is JSONContent {
  return isRecord(value) && value.type === 'doc' && isValidBlockArray(value.content);
}

function isValidBlockArray(value: unknown): boolean {
  return Array.isArray(value) && value.every(isValidBlock);
}

function isValidBlock(value: unknown): boolean {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return false;
  }

  if (value.type === 'paragraph') {
    return value.content === undefined || isValidInlineArray(value.content);
  }

  if (value.type === 'bulletList' || value.type === 'orderedList') {
    return Array.isArray(value.content) && value.content.every(isValidListItem);
  }

  return false;
}

function isValidListItem(value: unknown): boolean {
  if (!isRecord(value) || value.type !== 'listItem' || !Array.isArray(value.content)) {
    return false;
  }

  return value.content.every(item => {
    if (!isRecord(item)) {
      return false;
    }

    return item.type === 'paragraph'
      ? item.content === undefined || isValidInlineArray(item.content)
      : isValidBlock(item);
  });
}

function isValidInlineArray(value: unknown): boolean {
  return Array.isArray(value) && value.every(isValidInline);
}

function isValidInline(value: unknown): boolean {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return false;
  }

  if (value.type === 'hardBreak') {
    return true;
  }

  return (
    value.type === 'text' &&
    typeof value.text === 'string' &&
    (value.marks === undefined || isValidMarks(value.marks))
  );
}

function isValidMarks(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(mark => isRecord(mark) && typeof mark.type === 'string' && allowedMarks.has(mark.type))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
